import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, GripVertical, Download, Loader2, Share2, Copy, Check, ChevronDown, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

// Social media icons as SVG components
const TwitterIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const BeforeAfterSlider = ({ 
  beforeImage, 
  afterImage, 
  isOpen, 
  onClose,
  autoShow = false,
  autoShowDuration = 2500
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [downloadingType, setDownloadingType] = useState(null); // 'before' | 'after' | 'comparison' | null
  const [isSharing, setIsSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const containerRef = useRef(null);

  // Auto-show effect: briefly show comparison then close
  useEffect(() => {
    if (autoShow && isOpen) {
      const timer = setTimeout(() => {
        onClose?.();
      }, autoShowDuration);
      return () => clearTimeout(timer);
    }
  }, [autoShow, isOpen, onClose, autoShowDuration]);

  // Reset slider position when opened
  useEffect(() => {
    if (isOpen) {
      setSliderPosition(50);
      setCopied(false);
    }
  }, [isOpen]);

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  }, [isDragging, handleMove]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    handleMove(touch.clientX);
  }, [isDragging, handleMove]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Click anywhere on image to move slider
  const handleContainerClick = useCallback((e) => {
    if (isDragging) return;
    handleMove(e.clientX);
  }, [isDragging, handleMove]);

  // Download a single image (Before or After)
  const downloadSingleImage = useCallback(async (imageUrl, type) => {
    if (!imageUrl) {
      toast.error(`${type === 'after' ? 'Enhanced' : 'Original'} image not available`);
      return;
    }
    
    setDownloadingType(type);
    
    try {
      const filename = `mockuppro-${type}-${Date.now()}.png`;
      
      if (imageUrl.startsWith('data:')) {
        // Base64 image - direct download
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // URL image - fetch and download
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      
      toast.success(`${type === 'after' ? 'Enhanced' : 'Original'} image downloaded!`);
    } catch (error) {
      console.error(`Download ${type} error:`, error);
      toast.error(`Failed to download ${type === 'after' ? 'enhanced' : 'original'} image`);
    } finally {
      setDownloadingType(null);
    }
  }, []);

  // Download After image (primary action)
  const handleDownloadAfter = useCallback(() => {
    downloadSingleImage(afterImage, 'after');
  }, [afterImage, downloadSingleImage]);

  // Download Before image
  const handleDownloadBefore = useCallback(() => {
    downloadSingleImage(beforeImage, 'before');
  }, [beforeImage, downloadSingleImage]);

  // Create comparison canvas and return as blob
  const createComparisonBlob = useCallback(async () => {
    if (!beforeImage || !afterImage) return null;
    
    // Create canvas for side-by-side comparison
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Load both images
    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    };
    
    const [beforeImg, afterImg] = await Promise.all([
      loadImage(beforeImage),
      loadImage(afterImage)
    ]);
    
    // Calculate dimensions - use the larger of the two images
    const maxWidth = Math.max(beforeImg.width, afterImg.width);
    const maxHeight = Math.max(beforeImg.height, afterImg.height);
    
    // Canvas size: 2 images side by side + gap + padding for labels
    const gap = 20;
    const labelHeight = 60;
    const padding = 40;
    
    canvas.width = (maxWidth * 2) + gap + (padding * 2);
    canvas.height = maxHeight + labelHeight + (padding * 2);
    
    // Fill background
    ctx.fillStyle = '#1a1d23';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw Before image (centered in its half)
    const beforeX = padding + (maxWidth - beforeImg.width) / 2;
    const beforeY = padding + labelHeight + (maxHeight - beforeImg.height) / 2;
    ctx.drawImage(beforeImg, beforeX, beforeY);
    
    // Draw After image (centered in its half)
    const afterX = padding + maxWidth + gap + (maxWidth - afterImg.width) / 2;
    const afterY = padding + labelHeight + (maxHeight - afterImg.height) / 2;
    ctx.drawImage(afterImg, afterX, afterY);
    
    // Draw labels
    ctx.font = 'bold 24px Arial, sans-serif';
    ctx.textAlign = 'center';
    
    // Before label
    ctx.fillStyle = '#6b7280';
    ctx.fillText('BEFORE', padding + (maxWidth / 2), padding + 35);
    
    // After label
    ctx.fillStyle = '#22c55e';
    ctx.fillText('AFTER', padding + maxWidth + gap + (maxWidth / 2), padding + 35);
    
    // Draw divider line
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding + maxWidth + (gap / 2), padding);
    ctx.lineTo(padding + maxWidth + (gap / 2), canvas.height - padding);
    ctx.stroke();
    
    // Add branding watermark
    ctx.font = '14px Arial, sans-serif';
    ctx.fillStyle = '#6b7280';
    ctx.textAlign = 'right';
    ctx.fillText('Created with MockupPro', canvas.width - padding, canvas.height - 15);
    
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png', 0.95);
    });
  }, [beforeImage, afterImage]);

  // Download side-by-side comparison image
  const handleDownloadComparison = useCallback(async () => {
    setDownloadingType('comparison');
    
    try {
      const blob = await createComparisonBlob();
      
      if (!blob) {
        toast.error('Failed to create comparison image');
        return;
      }
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mockuppro-comparison-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Comparison image downloaded!');
      
    } catch (error) {
      console.error('Download comparison error:', error);
      toast.error('Failed to download comparison');
    } finally {
      setDownloadingType(null);
    }
  }, [createComparisonBlob]);

  // Native share using Web Share API
  const handleNativeShare = useCallback(async () => {
    setIsSharing(true);
    
    try {
      const blob = await createComparisonBlob();
      
      if (!blob) {
        toast.error('Failed to create comparison image');
        return;
      }
      
      const file = new File([blob], 'mockuppro-comparison.png', { type: 'image/png' });
      
      // Check if Web Share API with files is supported
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'Check out my AI enhancement!',
          text: 'See the before and after - created with MockupPro ✨',
          files: [file]
        });
        toast.success('Shared successfully!');
      } else if (navigator.share) {
        // Fallback to sharing without file
        await navigator.share({
          title: 'Check out my AI enhancement!',
          text: 'See the before and after - created with MockupPro ✨',
          url: 'https://mockuppro.org'
        });
        toast.success('Shared successfully!');
      } else {
        toast.error('Sharing not supported on this device');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
        toast.error('Failed to share');
      }
    } finally {
      setIsSharing(false);
    }
  }, [createComparisonBlob]);

  // Share to Twitter/X
  const handleShareTwitter = useCallback(() => {
    const text = encodeURIComponent('Check out this AI image enhancement! ✨ Before vs After - created with MockupPro');
    const url = encodeURIComponent('https://mockuppro.org');
    const hashtags = encodeURIComponent('AIart,MockupPro,ImageEnhancement');
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${url}&hashtags=${hashtags}`,
      '_blank',
      'width=550,height=420'
    );
    toast.success('Opening Twitter...');
  }, []);

  // Share to Facebook
  const handleShareFacebook = useCallback(() => {
    const url = encodeURIComponent('https://mockuppro.org');
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      '_blank',
      'width=550,height=420'
    );
    toast.success('Opening Facebook...');
  }, []);

  // Copy comparison image to clipboard
  const handleCopyToClipboard = useCallback(async () => {
    try {
      const blob = await createComparisonBlob();
      
      if (!blob) {
        toast.error('Failed to create comparison image');
        return;
      }
      
      // Try to copy image to clipboard
      if (navigator.clipboard && navigator.clipboard.write) {
        const item = new ClipboardItem({ 'image/png': blob });
        await navigator.clipboard.write([item]);
        setCopied(true);
        toast.success('Image copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback - download instead
        toast.error('Clipboard not supported. Try downloading instead.');
      }
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Failed to copy. Try downloading instead.');
    }
  }, [createComparisonBlob]);

  // Global mouse/touch event listeners for smooth dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Check if native sharing is available
  const canNativeShare = typeof navigator !== 'undefined' && navigator.share;
  
  // Check if currently downloading anything
  const isDownloading = downloadingType !== null;

  if (!beforeImage || !afterImage) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={onClose}
          data-testid="before-after-modal"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-4xl bg-background rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 p-2 bg-background/80 backdrop-blur-sm rounded-full hover:bg-background transition-colors"
              data-testid="close-comparison-btn"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Title */}
            <div className="absolute top-4 left-4 z-20 px-3 py-1.5 bg-background/80 backdrop-blur-sm rounded-full">
              <span className="text-sm font-medium">Before vs After</span>
            </div>

            {/* Auto-show indicator */}
            {autoShow && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 bg-accent/80 backdrop-blur-sm rounded-full">
                <span className="text-xs">Auto-closing...</span>
              </div>
            )}

            {/* Slider Container */}
            <div
              ref={containerRef}
              className="relative w-full aspect-square md:aspect-video cursor-ew-resize select-none touch-none"
              onClick={handleContainerClick}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            >
              {/* After Image (Background - Full width) */}
              <div className="absolute inset-0">
                <img
                  src={afterImage}
                  alt="After"
                  className="w-full h-full object-contain bg-muted/30"
                  draggable={false}
                />
                {/* After Label */}
                <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-green-500/90 text-white text-xs font-medium rounded-full shadow-lg">
                  After
                </div>
              </div>

              {/* Before Image (Clipped) */}
              <div 
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${sliderPosition}%` }}
              >
                <img
                  src={beforeImage}
                  alt="Before"
                  className="w-full h-full object-contain bg-muted/30"
                  style={{ 
                    width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100%',
                    maxWidth: 'none'
                  }}
                  draggable={false}
                />
                {/* Before Label */}
                <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-muted/90 text-foreground text-xs font-medium rounded-full shadow-lg">
                  Before
                </div>
              </div>

              {/* Slider Line & Handle */}
              <div
                className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
                style={{ left: `calc(${sliderPosition}% - 2px)` }}
              >
                {/* Slider Handle */}
                <div 
                  className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                    w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center
                    transition-transform duration-150 ${isDragging ? 'scale-110' : 'hover:scale-105'}`}
                  onMouseDown={handleMouseDown}
                  onTouchStart={handleTouchStart}
                >
                  <GripVertical className="w-5 h-5 text-muted-foreground" />
                </div>

                {/* Decorative arrows */}
                <div className="absolute top-1/2 -translate-y-1/2 -left-6 text-white/80 text-sm font-bold">
                  ◀
                </div>
                <div className="absolute top-1/2 -translate-y-1/2 -right-6 text-white/80 text-sm font-bold">
                  ▶
                </div>
              </div>
            </div>

            {/* Footer with Actions */}
            <div className="p-4 bg-muted/30">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground text-center sm:text-left">
                  Drag the slider to compare • 
                  <span className="text-green-600 font-medium ml-1">FREE</span>
                </p>
                
                <div className="flex items-center gap-2">
                  {/* Share Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isSharing}
                        className="h-9 px-3 bg-background hover:bg-accent/10 border-accent/30"
                        data-testid="share-dropdown-btn"
                      >
                        {isSharing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Share2 className="w-4 h-4" />
                        )}
                        <span className="ml-2 hidden sm:inline">Share</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      {/* Native Share (mobile) */}
                      {canNativeShare && (
                        <>
                          <DropdownMenuItem 
                            onClick={handleNativeShare}
                            className="cursor-pointer"
                            data-testid="native-share-btn"
                          >
                            <Share2 className="w-4 h-4 mr-2" />
                            Share...
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      
                      {/* Twitter/X */}
                      <DropdownMenuItem 
                        onClick={handleShareTwitter}
                        className="cursor-pointer"
                        data-testid="twitter-share-btn"
                      >
                        <TwitterIcon className="w-4 h-4 mr-2" />
                        Share on X
                      </DropdownMenuItem>
                      
                      {/* Facebook */}
                      <DropdownMenuItem 
                        onClick={handleShareFacebook}
                        className="cursor-pointer"
                        data-testid="facebook-share-btn"
                      >
                        <FacebookIcon className="w-4 h-4 mr-2" />
                        Share on Facebook
                      </DropdownMenuItem>
                      
                      <DropdownMenuSeparator />
                      
                      {/* Copy to Clipboard */}
                      <DropdownMenuItem 
                        onClick={handleCopyToClipboard}
                        className="cursor-pointer"
                        data-testid="copy-clipboard-btn"
                      >
                        {copied ? (
                          <>
                            <Check className="w-4 h-4 mr-2 text-green-500" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy to Clipboard
                          </>
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Download Dropdown - NEW */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="default"
                        size="sm"
                        disabled={isDownloading}
                        className="h-9 px-3 bg-green-600 hover:bg-green-700 text-white"
                        data-testid="download-dropdown-btn"
                      >
                        {isDownloading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="ml-2 hidden sm:inline">Downloading...</span>
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            <span className="ml-2 hidden sm:inline">Download</span>
                            <ChevronDown className="w-3 h-3 ml-1" />
                          </>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel className="text-xs text-muted-foreground">
                        Choose what to download
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      
                      {/* Download After (Primary - Default) */}
                      <DropdownMenuItem 
                        onClick={handleDownloadAfter}
                        disabled={!afterImage || downloadingType === 'after'}
                        className="cursor-pointer"
                        data-testid="download-after-btn"
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center">
                            <ImageIcon className="w-4 h-4 mr-2 text-green-600" />
                            <span>Download After</span>
                          </div>
                          <span className="text-xs text-green-600 font-medium">Enhanced</span>
                        </div>
                      </DropdownMenuItem>
                      
                      {/* Download Before */}
                      <DropdownMenuItem 
                        onClick={handleDownloadBefore}
                        disabled={!beforeImage || downloadingType === 'before'}
                        className="cursor-pointer"
                        data-testid="download-before-btn"
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center">
                            <ImageIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                            <span>Download Before</span>
                          </div>
                          <span className="text-xs text-muted-foreground">Original</span>
                        </div>
                      </DropdownMenuItem>
                      
                      <DropdownMenuSeparator />
                      
                      {/* Download Comparison */}
                      <DropdownMenuItem 
                        onClick={handleDownloadComparison}
                        disabled={downloadingType === 'comparison'}
                        className="cursor-pointer"
                        data-testid="download-comparison-btn"
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center">
                            <GripVertical className="w-4 h-4 mr-2 text-blue-600" />
                            <span>Download Comparison</span>
                          </div>
                          <span className="text-xs text-blue-600">Side-by-side</span>
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              
              {/* Download hint */}
              <p className="text-xs text-muted-foreground/70 text-center mt-2">
                Download the enhanced version or share your transformation!
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BeforeAfterSlider;
